import React, { useEffect, useState, useMemo } from "react";

import Backgrounds from "../../_partials/Backgrounds/Index";
import TemplateBlock from "../../_partials/TemplateBlock/Index";
import prepareVideoBg from "../../../../shared/functions/prepareVideoBg";
import animationScript, { animationCleanUp } from "./animation";
import getContentById from "../../../../shared/utils/getContentById";

import assets from "./assets";
import { useTranslation } from "react-i18next";
import Image from "../../../../shared/components/Image";
import ProgressBar from "../../../../shared/components/ProgressBar";
import { MatchScoreWidget } from "../../_partials/MatchScoreWidget/MatchScoreWidgetForBild";

import "./style/style.scss";
import "./style/animation.scss";
import { getDynamicLogoPath } from "../../../../shared/utils/functions";
import getOptaCountryCode from "../../../../shared/utils/getOptaCountryCode";
import bild_zone_pitch from "../../../assets/wc/videos/heatmap.webm";
import HotZoneGraph from "./HotZoneGraph";

const BACKGROUND_PATH = "./assets/background_1920";
const COLUMNS = 7;
const COL_WIDTH = 100 / COLUMNS;
// const ROW_HEIGHTS = { top: 25, mid: 50, bottom: 25 };
const ROW_HEIGHTS = { z1: 20, z2: 20, z3: 20, z4: 20, z5: 20 };

const getMainBackground = () => {
  return assets.background_1920;
};

export default function Index({ animation, data, mode, format, ...props }) {
  const refs = {
    player: React.createRef(),
    team: React.createRef(),
  };

  const { t } = useTranslation();

  const { template_name, uuid, data: templateData } = data;
  const [shots, setShots] = useState(
    Array(COLUMNS).fill({ z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 }),
  );

  const templateName = template_name ?? "";
  const id = `${templateName}_${uuid}`;
  const backgroundImage = BACKGROUND_PATH;

  const { matchDetails, goalShots, optaMatchInfo: matchInfo, matchInfo: bildMatchInfo, teamRanking } = templateData?.dynamic ?? {};
  const matchStatus = matchDetails?.matchStatus;
  const scores = matchDetails?.scores;

  const fields = templateData?.fields || [];
  const competitionId = matchInfo?.competition?.id;


  const homeTeam = matchInfo?.contestant?.[0];
  const awayTeam = matchInfo?.contestant?.[1];

  const teamHomeAlpha2 = getOptaCountryCode(
    homeTeam?.name,
    homeTeam?.shortName,
    homeTeam?.code,
  );
  const teamAwayAlpha2 = getOptaCountryCode(
    awayTeam?.name,
    awayTeam?.shortName,
    awayTeam?.code,
  );

  const home = matchInfo?.contestant?.[0];
  const away = matchInfo?.contestant?.[1];
  const teamSide = getContentById("selectedSide", fields, "value") ?? "home";
  const team = teamSide === "home" ? homeTeam : awayTeam;

  const teamId = teamSide === "home" ? home?.id : away?.id;
  const calculateShotPositions = (shots) => {
    const shortsObj = Array.from({ length: COLUMNS }, () => ({
      z1: 0,
      z2: 0,
      z3: 0,
      z4: 0,
      z5: 0,
    }));

    const zoneLimits = [
      ROW_HEIGHTS.z1,
      ROW_HEIGHTS.z1 + ROW_HEIGHTS.z2,
      ROW_HEIGHTS.z1 + ROW_HEIGHTS.z2 + ROW_HEIGHTS.z3,
      ROW_HEIGHTS.z1 + ROW_HEIGHTS.z2 + ROW_HEIGHTS.z3 + ROW_HEIGHTS.z4,
      100,
    ];

    shots.forEach(({ x, y }) => {
      let colIndex = Math.floor(x / COL_WIDTH);
      if (teamSide === "away") {
        colIndex = COLUMNS - colIndex - 1;
      }

      let rowKey = "z5";
      if (y <= zoneLimits[0]) rowKey = "z1";
      else if (y <= zoneLimits[1]) rowKey = "z2";
      else if (y <= zoneLimits[2]) rowKey = "z3";
      else if (y <= zoneLimits[3]) rowKey = "z4";

      if (shortsObj[colIndex]) {
        shortsObj[colIndex][rowKey]++;
      }
    });

    if (teamSide === "home") {
      shortsObj[0] = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
    } else if (teamSide === "away") {
      shortsObj[COLUMNS - 1] = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
    }
    return shortsObj;
  };

  const shotCoordinates = useMemo(() => {
    return (
      goalShots
        ?.filter((shot) => shot.contestantId === teamId)
        .map(({ destPosX, destPosY }) => ({ x: destPosX, y: destPosY })) ?? []
    );
  }, [goalShots, teamId]);
  const [showHotzoneGraph, setShowHotzoneGraph] = useState(false);


  useEffect(() => {
    if (mode === "animation" || mode === "video") {
      const timeout = setTimeout(() => {
        setShowHotzoneGraph(true);
        // 🔥 Fade in background pitch image when animation starts
        setTimeout(() => {
          const pitchImage = document.querySelector(`#${id} .hotzone-pitch`);
          if (pitchImage) {
            pitchImage.style.opacity = "1";
          }
          const arrowsHeatmap = document.querySelector(`#${id} .bild-hotzone-arrows-right`);
          if (arrowsHeatmap) {
            arrowsHeatmap.style.opacity = "0.2";
          }
        }, 100);

      }, 2500); // Show after 2s
      return () => clearTimeout(timeout);
    } else {
      setShowHotzoneGraph(false); // Don't show in preview/edit
    }
  }, [mode]);

  useEffect(() => {
    if (!shotCoordinates.length) {
      setShots(Array(COLUMNS).fill({ upper: 0, middle: 0, lower: 0 }));
      return;
    }
    const updatedShots = calculateShotPositions(shotCoordinates);
    setShots(updatedShots);
  }, [shotCoordinates]);

  const isTimeline = props.isTimeline;
  const prepareAnimation = () => {
    prepareVideoBg({ id });
  };
  useEffect(() => {
    let animationReturn = null;
    if (mode === "animation" || mode === "video") {
      if (animation) {
        prepareAnimation();
        animationReturn = animationScript({
          refs,
          data,
          animation,
          mode,
          id,
          format,
          apiSetup: props?.apiSetup,
          templateName: data?.template_name
        });
      }
    }

    return function cleanUp() {
      if (animationReturn) {
        animationCleanUp(animationReturn);
      }
    };
  }, [animation, mode]);

  const bildHomeTeam = bildMatchInfo?.home;
  const bildAwayTeam = bildMatchInfo?.away;
  return (
    <div
      id={id}
      className={`T_${templateName} ${animation ? "animation" : ""} side-${teamSide}`}
    >
      <TemplateBlock class={`${isTimeline ? "" : "template-bild"}`}>
        {
          !isTimeline && <div className="main-header-content">
            <div className="main-header">
              {t("Hot zone", { keyPrefix: "templates" })}
            </div>
            <MatchScoreWidget
              homeTeam={bildHomeTeam}
              awayTeam={bildAwayTeam}
              matchInfo={bildMatchInfo}
            />
          </div>
        }
        <div className="wc-content wc-content-v2">
          <ProgressBar height="8px" />

          <div className="main-content-side">
            <div className={"left-block-side"}>
              <div className="teams-block">
                <div className={`team-logo-container ${teamSide}`}>
                  <Image
                    className="team-logo"
                    src={getDynamicLogoPath({ team: team, competitionId })}
                  />
                </div>
                <div className={`team-name ${teamSide}`}>
                  {t(team?.name, {
                    ns: "countries",
                  })}
                </div>
              </div>
              <div className="left-block-shadow"></div>
            </div>

            <div className={"center-block-side"}>
              <div
                className={`hotzone-block`}
              >
                <video
                  className="video-pitch"
                  width="100%"
                  height="100%"
                  controls={false}
                  muted={true}
                  autoPlay={false}
                  loop={false}>
                  <source src={bild_zone_pitch} type="video/mp4" />
                </video>

                {showHotzoneGraph && (
                  <div className="hotzone-fade-in">
                    <HotZoneGraph
                      data={{
                        homeTeam: home,
                        awayTeam: away,
                        competitionId,
                        shots,
                        teamSide,
                      }}
                    />
                    <div className={"bild-hotzone-arrows-right"}>
                      <img src={assets.right_arrow} />
                      <img src={assets.right_arrow} />
                      <img src={assets.right_arrow} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </TemplateBlock>
    </div>
  );
}
